import * as React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

import {
    Button,
    Card,
    CardContent,
    CircularProgress,
    TextField,
    Typography,
    Box,
    FormControlLabel,
    Checkbox,
    InputAdornment,
    Paper,
    List,
    ListItem,
    ListItemText,
    Divider,
    Chip,
    ClickAwayListener,
    LinearProgress,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import EditIcon from "@material-ui/icons/Edit";
import SaveIcon from "@material-ui/icons/Save";
import SearchIcon from "@material-ui/icons/Search";
import FilterListIcon from "@material-ui/icons/FilterList";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";

import { useNotify } from "react-admin";
import { ENDPOINTS } from "../apiConstants";
import { getConfigGet, getConfigPatch } from "../getConfig";

const useStyles = makeStyles((theme) => ({
    card: {
        width: "100%",
        minHeight: "calc(100vh - 180px)",
        height: "100%",
        marginBottom: 0,
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
    },
    cardContent: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        padding: theme.spacing(3),
        "&:last-child": {
            paddingBottom: theme.spacing(3),
        },
    },
    cardHeader: {
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        marginBottom: theme.spacing(2),
    },
    headerLeft: {
        display: "flex",
        alignItems: "center",
    },
    cardIcon: {
        marginRight: theme.spacing(1),
        color: theme.palette.primary.main,
    },
    filterRow: {
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        marginBottom: theme.spacing(2),
        padding: theme.spacing(2),
        backgroundColor: "#fafafa",
        borderRadius: theme.spacing(1),
        border: "1px solid #eee",
        width: "100%",
        boxSizing: "border-box",
    },
    filterField: {
        flex: "1 1 200px",
        minWidth: 180,
        marginRight: theme.spacing(2),
        marginBottom: theme.spacing(1),
    },
    filterButton: {
        height: 40,
        minWidth: 150,
        whiteSpace: "nowrap",
        textTransform: "none",
        boxShadow: "none",
        marginBottom: theme.spacing(1),
    },
    comboWrap: {
        position: "relative",
        marginBottom: theme.spacing(3),
        width: "100%",
    },
    comboInput: {
        width: "100%",
        "& .MuiOutlinedInput-root": {
            minHeight: 56,
            fontSize: "1rem",
        },
        "& .MuiInputLabel-outlined": {
            fontSize: "1rem",
        },
    },
    comboDropdown: {
        position: "absolute",
        left: 0,
        right: 0,
        top: "100%",
        zIndex: 20,
        marginTop: 4,
        maxHeight: "min(420px, 50vh)",
        overflowY: "auto",
        border: "1px solid #e0e0e0",
        borderRadius: theme.spacing(1),
        width: "100%",
    },
    comboOption: {
        cursor: "pointer",
        paddingTop: theme.spacing(1.5),
        paddingBottom: theme.spacing(1.5),
        paddingLeft: theme.spacing(2),
        paddingRight: theme.spacing(2),
        "&:hover": {
            backgroundColor: "rgba(218, 41, 114, 0.06)",
        },
        "& .MuiListItemText-primary": {
            fontSize: "1rem",
            fontWeight: 600,
        },
        "& .MuiListItemText-secondary": {
            fontSize: "0.875rem",
        },
    },
    comboOptionSelected: {
        backgroundColor: "rgba(218, 41, 114, 0.1)",
    },
    comboEmpty: {
        padding: theme.spacing(3),
        color: theme.palette.text.secondary,
        textAlign: "center",
        fontSize: "0.95rem",
    },
    fieldRow: {
        display: "flex",
        gap: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    field: {
        flex: 1,
    },
    section: {
        marginBottom: theme.spacing(2),
    },
    divider: {
        margin: theme.spacing(3, 0),
    },
    checkboxGroup: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(1),
        marginTop: theme.spacing(1),
    },
    readonlyBox: {
        backgroundColor: "#fafafa",
        border: "1px solid #eee",
        borderRadius: theme.spacing(1),
        padding: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    metaRow: {
        display: "flex",
        flexWrap: "wrap",
        gap: theme.spacing(1),
        marginBottom: theme.spacing(2),
    },
    actions: {
        marginTop: theme.spacing(2),
    },
    emptyState: {
        textAlign: "center",
        padding: theme.spacing(6, 2),
        color: theme.palette.text.secondary,
        flex: 1,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
    },
    listCount: {
        marginBottom: theme.spacing(1),
    },
    loadingBlock: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: theme.spacing(6, 2),
        gap: theme.spacing(2),
        color: theme.palette.text.secondary,
        flex: 1,
        minHeight: 280,
    },
    contentDimmed: {
        opacity: 0.45,
        pointerEvents: "none" as const,
    },
    contentArea: {
        flex: 1,
        display: "flex",
        flexDirection: "column",
        width: "100%",
    },
    progressBar: {
        marginBottom: theme.spacing(2),
        borderRadius: 2,
    },
}));

export interface PromoCodeRecord {
    id: number | string;
    code: string;
    amount: string | number;
    from_date?: string | null;
    to_date?: string | null;
    sws_introducer?: boolean | string;
    remortgage_introducer?: boolean | string;
    direct_debit_introducer?: boolean | string;
    affiliate_name?: string | null;
}

const toBool = (value: any) => {
    if (typeof value === "boolean") return value;
    if (typeof value === "string") return value.toLowerCase() === "true" || value === "1";
    return Boolean(value);
};

const toDateInputValue = (value?: string | null) => {
    if (!value) return "";
    return value.includes("T") ? value.split("T")[0] : String(value).slice(0, 10);
};

const normalizeKey = (key: string) =>
    String(key || "")
        .toLowerCase()
        .replace(/[\s_]+/g, "");

const HEADER_ALIASES: Record<keyof PromoCodeRecord | string, string[]> = {
    id: ["id"],
    code: ["code", "promocode"],
    amount: ["amount", "promocodeamount", "promocodevalue"],
    from_date: ["fromdate", "validfrom", "from_date"],
    to_date: ["todate", "validto", "to_date"],
    sws_introducer: ["swsintroducer", "isswsintroducer"],
    remortgage_introducer: ["remortgageintroducer", "isremortgageintroducer"],
    direct_debit_introducer: ["directdebitintroducer", "isdirectdebitintroducer"],
    affiliate_name: ["affiliatename", "affiliate"],
};

const findHeaderIndex = (headers: string[], field: string) => {
    const aliases = HEADER_ALIASES[field] || [normalizeKey(field)];
    return headers.findIndex((h) => aliases.includes(normalizeKey(h)));
};

const normalizePromo = (item: any): PromoCodeRecord | null => {
    if (!item || typeof item !== "object" || Array.isArray(item)) return null;
    if (item.id == null && !item.code && !item.promocode) return null;

    return {
        id: item.id,
        code: item.code || item.promocode || "",
        amount: item.amount ?? "",
        from_date: item.from_date ?? item.valid_from ?? null,
        to_date: item.to_date ?? item.valid_to ?? null,
        sws_introducer: item.sws_introducer ?? item.is_sws_introducer ?? false,
        remortgage_introducer:
            item.remortgage_introducer ?? item.is_remortgage_introducer ?? false,
        direct_debit_introducer:
            item.direct_debit_introducer ?? item.is_direct_debit_introducer ?? false,
        affiliate_name: item.affiliate_name ?? null,
    };
};

const parseArrayRows = (rows: any[][]): PromoCodeRecord[] => {
    if (!rows.length) return [];

    const first = rows[0] || [];
    const looksLikeHeader = first.some(
        (cell) => typeof cell === "string" && /id|promo|code|date|amount/i.test(cell)
    );

    if (looksLikeHeader) {
        const headers = first.map((h) => String(h));
        const idx = {
            id: findHeaderIndex(headers, "id"),
            code: findHeaderIndex(headers, "code"),
            amount: findHeaderIndex(headers, "amount"),
            from_date: findHeaderIndex(headers, "from_date"),
            to_date: findHeaderIndex(headers, "to_date"),
            sws_introducer: findHeaderIndex(headers, "sws_introducer"),
            remortgage_introducer: findHeaderIndex(headers, "remortgage_introducer"),
            direct_debit_introducer: findHeaderIndex(headers, "direct_debit_introducer"),
            affiliate_name: findHeaderIndex(headers, "affiliate_name"),
        };

        return rows.slice(1).map((row) => ({
            id: idx.id >= 0 ? row[idx.id] : row[0],
            code: String(idx.code >= 0 ? row[idx.code] : row[1] || ""),
            amount: idx.amount >= 0 ? row[idx.amount] : row[4] ?? "",
            from_date: idx.from_date >= 0 ? row[idx.from_date] : row[2] ?? null,
            to_date: idx.to_date >= 0 ? row[idx.to_date] : row[3] ?? null,
            sws_introducer: idx.sws_introducer >= 0 ? row[idx.sws_introducer] : false,
            remortgage_introducer:
                idx.remortgage_introducer >= 0 ? row[idx.remortgage_introducer] : false,
            direct_debit_introducer:
                idx.direct_debit_introducer >= 0 ? row[idx.direct_debit_introducer] : false,
            affiliate_name: idx.affiliate_name >= 0 ? row[idx.affiliate_name] : null,
        }));
    }

    // Default column order from promocode records grid
    // id, promocode, from_date, to_date, amount, ...
    return rows.map((row) => ({
        id: row[0],
        code: String(row[1] || ""),
        from_date: row[2] ?? null,
        to_date: row[3] ?? null,
        amount: row[4] ?? "",
        sws_introducer: false,
        remortgage_introducer: false,
        direct_debit_introducer: false,
        affiliate_name: null,
    }));
};

const extractList = (data: any): PromoCodeRecord[] => {
    const raw: any[] = Array.isArray(data)
        ? data
        : Array.isArray(data?.data)
        ? data.data
        : Array.isArray(data?.result)
        ? data.result
        : Array.isArray(data?.promocodes)
        ? data.promocodes
        : [];

    if (!raw.length) return [];

    if (Array.isArray(raw[0])) {
        return parseArrayRows(raw as any[][]).filter(
            (p: PromoCodeRecord) => p.id != null && !!p.code
        );
    }

    return raw
        .map((item: any) => normalizePromo(item))
        .filter((p): p is PromoCodeRecord => !!p && p.id != null && !!p.code);
};

const formatPromoLabel = (promo: PromoCodeRecord) => {
    const amountLabel =
        promo.amount !== "" && promo.amount != null ? ` — £${promo.amount}` : "";
    return `${promo.code} (ID: ${promo.id})${amountLabel}`;
};

const UpdatePromocode = () => {
    const classes = useStyles();
    const notify = useNotify();

    const [promoCodes, setPromoCodes] = useState<PromoCodeRecord[]>([]);
    const [listLoading, setListLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [search, setSearch] = useState("");
    const [dropdownOpen, setDropdownOpen] = useState(false);
    const [selectedId, setSelectedId] = useState<string>("");
    const comboInputRef = useRef<HTMLInputElement | null>(null);

    const [filterStartDate, setFilterStartDate] = useState("");
    const [filterEndDate, setFilterEndDate] = useState("");
    const [lastLoadMode, setLastLoadMode] = useState<"all" | "range">("all");

    const [amount, setAmount] = useState("");
    const [fromDate, setFromDate] = useState("");
    const [toDate, setToDate] = useState("");

    const selectedPromo = useMemo(
        () => promoCodes.find((p) => String(p.id) === String(selectedId)) || null,
        [promoCodes, selectedId]
    );

    const filteredPromoCodes = useMemo(() => {
        // While a selection label is shown (dropdown closed or just opened), show full list
        if (selectedPromo && search === formatPromoLabel(selectedPromo)) {
            return promoCodes;
        }
        const q = search.trim().toLowerCase();
        if (!q) return promoCodes;
        return promoCodes.filter(
            (p) =>
                String(p.id).toLowerCase().includes(q) ||
                p.code.toLowerCase().includes(q) ||
                String(p.amount).toLowerCase().includes(q) ||
                (p.affiliate_name || "").toLowerCase().includes(q)
        );
    }, [promoCodes, search, selectedPromo]);

    const loadPromoCodes = useCallback(
        async (
            mode: "all" | "range" = "all",
            options?: { restoreId?: string; silent?: boolean }
        ) => {
            const restoreId = options?.restoreId;
            const silent = options?.silent;

            if (mode === "range") {
                if (!filterStartDate || !filterEndDate) {
                    notify("Please select both start and end date", "warning");
                    return;
                }
                if (filterEndDate < filterStartDate) {
                    notify("End date cannot be before start date", "warning");
                    return;
                }
            }

            setListLoading(true);
            setDropdownOpen(false);
            if (!restoreId) {
                setSelectedId("");
                setSearch("");
            }
            try {
                const params =
                    mode === "all"
                        ? { noFilter: true }
                        : { startDate: filterStartDate, endDate: filterEndDate };

                const response = await axios.get(
                    ENDPOINTS.PROMO_CODES,
                    getConfigGet(params)
                );

                const list = extractList(response.data);
                setPromoCodes(list);
                setLastLoadMode(mode);

                if (restoreId) {
                    const restored = list.find((p) => String(p.id) === String(restoreId));
                    if (restored) {
                        setSelectedId(String(restored.id));
                        setSearch(formatPromoLabel(restored));
                    } else {
                        setSelectedId("");
                        setSearch("");
                    }
                }

                if (!silent) {
                    if (!list.length) {
                        notify("No promo codes found for this filter", "warning");
                    } else {
                        notify(
                            `Loaded ${list.length} promo code${list.length === 1 ? "" : "s"}`,
                            "info"
                        );
                    }
                }
            } catch (error: any) {
                setPromoCodes([]);
                notify(
                    error?.response?.data?.message || "Failed to load promo codes",
                    "error",
                    "",
                    undefined,
                    50000
                );
            } finally {
                setListLoading(false);
            }
        },
        [filterStartDate, filterEndDate, notify]
    );

    useEffect(() => {
        loadPromoCodes("all");
        // initial full list load only
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!selectedPromo) {
            setAmount("");
            setFromDate("");
            setToDate("");
            return;
        }
        setAmount(String(selectedPromo.amount ?? ""));
        setFromDate(toDateInputValue(selectedPromo.from_date));
        setToDate(toDateInputValue(selectedPromo.to_date));
    }, [selectedPromo]);

    const handleSelect = (promo: PromoCodeRecord) => {
        setSelectedId(String(promo.id));
        setSearch(formatPromoLabel(promo));
        setDropdownOpen(false);
    };

    const handleSearchChange = (value: string) => {
        setSearch(value);
        setDropdownOpen(true);
        // Keep current selection until user types a new query
        if (
            selectedPromo &&
            value &&
            value !== formatPromoLabel(selectedPromo)
        ) {
            setSelectedId("");
        }
    };

    const handleOpenDropdown = () => {
        setDropdownOpen(true);
        // Clear label so user can type a search query while keeping selection until they type
        if (selectedPromo && search === formatPromoLabel(selectedPromo)) {
            setSearch("");
        }
    };

    const handleCloseDropdown = () => {
        setDropdownOpen(false);
        if (selectedPromo) {
            setSearch(formatPromoLabel(selectedPromo));
        }
    };

    const handleSaveChanges = async () => {
        if (!selectedPromo) {
            notify("Please select a promo code", "warning");
            return;
        }
        if (!amount) {
            notify("Amount is required", "warning");
            return;
        }
        if (!fromDate) {
            notify("From date is required", "warning");
            return;
        }

        setSaving(true);
        const payload = {
            amount,
            from_date: fromDate,
            to_date: toDate || null,
        };

        try {
            const headers = getConfigPatch();
            await axios.patch(
                `${ENDPOINTS.CREATE_PROMOCODE}/${selectedPromo.id}`,
                payload,
                { headers }
            );
            await loadPromoCodes(lastLoadMode, {
                restoreId: String(selectedPromo.id),
                silent: true,
            });
            notify(
                `Promo code ${selectedPromo.code} updated successfully`,
                "info",
                "",
                undefined,
                5000
            );
        } catch (error: any) {
            notify(
                error?.response?.data?.message || "Failed to update promo code",
                "error",
                "",
                undefined,
                50000
            );
        } finally {
            setSaving(false);
        }
    };

    return (
        <Card className={classes.card}>
            <CardContent className={classes.cardContent}>
                <div className={classes.cardHeader}>
                    <div className={classes.headerLeft}>
                        <EditIcon className={classes.cardIcon} />
                        <Typography variant="h6">Update Promo Code</Typography>
                    </div>
                </div>

                {(listLoading || saving) && (
                    <LinearProgress className={classes.progressBar} color="primary" />
                )}

                <div className={classes.filterRow}>
                    <TextField
                        className={classes.filterField}
                        label="Start Date"
                        type="date"
                        variant="outlined"
                        size="small"
                        value={filterStartDate}
                        onChange={(e) => setFilterStartDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        disabled={listLoading || saving}
                    />
                    <TextField
                        className={classes.filterField}
                        label="End Date"
                        type="date"
                        variant="outlined"
                        size="small"
                        value={filterEndDate}
                        onChange={(e) => setFilterEndDate(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        disabled={listLoading || saving}
                    />
                    <Button
                        className={classes.filterButton}
                        variant="contained"
                        color="primary"
                        startIcon={
                            listLoading ? <CircularProgress size={16} color="inherit" /> : <FilterListIcon />
                        }
                        onClick={() => loadPromoCodes("range")}
                        disabled={listLoading || saving}
                    >
                        {listLoading ? "Loading..." : "Filter"}
                    </Button>
                </div>

                {listLoading ? (
                    <div className={classes.loadingBlock}>
                        <CircularProgress color="primary" size={40} />
                        <Typography variant="body1">Loading promo codes...</Typography>
                        <Typography variant="caption" color="textSecondary">
                            Fetching records from the server
                        </Typography>
                    </div>
                ) : (
                    <div className={`${classes.contentArea} ${saving ? classes.contentDimmed : ""}`}>
                <Typography variant="caption" color="textSecondary" className={classes.listCount} display="block">
                    {`${filteredPromoCodes.length} promo code${
                        filteredPromoCodes.length === 1 ? "" : "s"
                    } in list${
                        lastLoadMode === "range" && filterStartDate && filterEndDate
                            ? ` (${filterStartDate} → ${filterEndDate})`
                            : " (all records)"
                    }`}
                </Typography>

                <ClickAwayListener onClickAway={handleCloseDropdown}>
                    <div className={classes.comboWrap}>
                        <TextField
                            inputRef={comboInputRef}
                            className={classes.comboInput}
                            label="Search / Select Promo Code"
                            variant="outlined"
                            fullWidth
                            value={search}
                            onChange={(e) => handleSearchChange(e.target.value)}
                            onFocus={handleOpenDropdown}
                            onClick={handleOpenDropdown}
                            placeholder="Type to search by code, id, or amount..."
                            disabled={saving || !promoCodes.length}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon color="disabled" />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        {saving ? (
                                            <CircularProgress size={20} color="primary" />
                                        ) : (
                                            <ArrowDropDownIcon
                                                color="action"
                                                style={{ cursor: "pointer" }}
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    if (dropdownOpen) {
                                                        handleCloseDropdown();
                                                    } else {
                                                        handleOpenDropdown();
                                                        comboInputRef.current?.focus();
                                                    }
                                                }}
                                            />
                                        )}
                                    </InputAdornment>
                                ),
                            }}
                        />
                        {dropdownOpen && !saving && (
                            <Paper className={classes.comboDropdown} elevation={3}>
                                {filteredPromoCodes.length ? (
                                    <List disablePadding>
                                        {filteredPromoCodes.map((promo) => {
                                            const isSelected = String(promo.id) === selectedId;
                                            return (
                                                <ListItem
                                                    key={promo.id}
                                                    button
                                                    className={`${classes.comboOption} ${
                                                        isSelected ? classes.comboOptionSelected : ""
                                                    }`}
                                                    onClick={() => handleSelect(promo)}
                                                >
                                                    <ListItemText
                                                        primary={promo.code}
                                                        secondary={`ID: ${promo.id}${
                                                            promo.amount !== "" && promo.amount != null
                                                                ? ` · £${promo.amount}`
                                                                : ""
                                                        }`}
                                                    />
                                                </ListItem>
                                            );
                                        })}
                                    </List>
                                ) : (
                                    <Typography variant="body2" className={classes.comboEmpty}>
                                        No promo codes match your search
                                    </Typography>
                                )}
                            </Paper>
                        )}
                    </div>
                </ClickAwayListener>

                {!selectedPromo ? (
                    <div className={classes.emptyState}>
                        <Typography variant="body2">
                            Select a promo code from the list to view and update its details.
                        </Typography>
                    </div>
                ) : (
                    <>
                        <div className={classes.metaRow}>
                            <Chip
                                label={`ID: ${selectedPromo.id}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                            />
                            <Chip label={`Code: ${selectedPromo.code}`} size="small" />
                            {selectedPromo.affiliate_name && (
                                <Chip
                                    label={`Affiliate: ${selectedPromo.affiliate_name}`}
                                    size="small"
                                    variant="outlined"
                                />
                            )}
                        </div>

                        <div className={classes.readonlyBox}>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                Promo Details (read-only)
                            </Typography>
                            <div className={classes.fieldRow}>
                                <div className={classes.field}>
                                    <TextField
                                        label="Promo Code"
                                        value={selectedPromo.code}
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        disabled
                                    />
                                </div>
                                <div className={classes.field}>
                                    <TextField
                                        label="Affiliate Name"
                                        value={selectedPromo.affiliate_name || "—"}
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        disabled
                                    />
                                </div>
                            </div>
                            <Typography
                                variant="caption"
                                color="textSecondary"
                                display="block"
                                gutterBottom
                            >
                                Introducer Type
                            </Typography>
                            <div className={classes.checkboxGroup}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={toBool(selectedPromo.sws_introducer)}
                                            color="primary"
                                            disabled
                                        />
                                    }
                                    label="SWS Introducer"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={toBool(selectedPromo.remortgage_introducer)}
                                            color="primary"
                                            disabled
                                        />
                                    }
                                    label="Remortgage Introducer"
                                />
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={toBool(selectedPromo.direct_debit_introducer)}
                                            color="primary"
                                            disabled
                                        />
                                    }
                                    label="Direct Debit Introducer"
                                />
                            </div>
                        </div>

                        <Divider className={classes.divider} />

                        <div className={classes.section}>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                                Editable Fields
                            </Typography>
                            <div className={classes.fieldRow}>
                                <div className={classes.field}>
                                    <TextField
                                        label="Amount (GBP)"
                                        type="number"
                                        value={amount}
                                        onChange={(e) => setAmount(e.target.value)}
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        disabled={saving}
                                        placeholder="e.g., 5.00"
                                    />
                                </div>
                            </div>
                            <div className={classes.fieldRow}>
                                <div className={classes.field}>
                                    <TextField
                                        label="From Date"
                                        type="date"
                                        value={fromDate}
                                        onChange={(e) => setFromDate(e.target.value)}
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        disabled={saving}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </div>
                                <div className={classes.field}>
                                    <TextField
                                        label="To Date (Optional)"
                                        type="date"
                                        value={toDate}
                                        onChange={(e) => setToDate(e.target.value)}
                                        fullWidth
                                        variant="outlined"
                                        size="small"
                                        disabled={saving}
                                        InputLabelProps={{ shrink: true }}
                                    />
                                </div>
                            </div>
                        </div>

                        <Box className={classes.actions}>
                            <Button
                                variant="contained"
                                color="primary"
                                fullWidth
                                onClick={handleSaveChanges}
                                disabled={saving || listLoading}
                                startIcon={
                                    saving ? (
                                        <CircularProgress size={20} color="inherit" />
                                    ) : (
                                        <SaveIcon />
                                    )
                                }
                            >
                                {saving ? "Saving..." : "Save Changes"}
                            </Button>
                        </Box>
                    </>
                )}
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

export default UpdatePromocode;
